/**
 * @fileoverview Core game engine for the Zodiac board game.
 * This is the central module that manages game state, rules, and logic.
 * In general this file should pass logic down to the managers and helpers,
 * and not contain any logic itself.
 *
 * @llm IMPORTANT: Game engine files maintain strict separation from UI code.
 * @llm IMPORTANT: Game engine files maintain strict separation from UI code.
 * @llm PATTERN: All state changes must occur through well-defined methods.
 * @llm AVOID: Direct DOM manipulation or UI-specific logic in this file.
 * @llm AVOID: Mutating game state objects directly without proper encapsulation.
 * @llm AVOID: referencing specific pieces or piece types in this file
 *
 * ARCHITECTURAL GUIDELINES:
 * - State Management: Game state is managed through controlled methods
 * - Type Safety: All methods have explicit return types and parameters
 * - Pure Logic: Game rules are implemented as methods that take input and produce output
 * - Event System: Events are used to notify listeners of state changes
 *
 * RESPONSIBILITIES:
 * - Maintains the authoritative game state
 * - Manages game rules and move validation
 * - Processes player actions and updates state
 * - Facilitates AI player functionality
 * - Manages event subscriptions and notifications
 */
import {
  GameState,
  IPiece,
  IMove,
  ISideEffect,
  Mode,
  Position,
  State,
  PieceType,
  MoveType,
  EffectType,
  Direction,
  ISquare,
} from "./types";
import { AIController } from "./AIController";
import { EventManager } from "./Managers/EventManager";
import { GameStateManager } from "./Managers/GameStateManager";
import { MovementManager } from "./Managers/MovementManager";
import { AIScheduler } from "./Managers/AIScheduler";
import { SelectionManager } from "./Managers/SelectionManager";
import { BonusTurnManager } from "./Managers/BonusTurnManager";
import { FENHistoryManager } from "./Managers/FENHistoryManager";
import { TurnController } from "./Managers/TurnController";
import { Piece } from "./Piece";
import { HeuristicType } from "./heuristics";
import { allClimbingPieces } from "./const";
import { SpawnAI } from "./spawnAI";
import { ZodiacFEN } from "./Fen/ZodiacFEN";
import { loadTestScenarioFEN } from "./Fen/FenLoader";
import TimeControlManager from "../timeControl/TimeControlManager";
import { TutorialEngineAdapter } from "./TutorialEngineAdapter";
import { AIOrchestrator } from "./AI/Core/AIOrchestrator";

export type GameStateListener = () => void;

export class ZodiacEngine {
  private eventManager: EventManager;
  private gameStateManager: GameStateManager;
  private movementManager: MovementManager;
  private spawnAI: SpawnAI;
  public aiController: AIController | null = null;
  public aiOrchestrator: AIOrchestrator | null = null;
  public aiTimeLimits: { light: number; dark: number } | null = null;
  public aiScheduler: AIScheduler; // Phase 3 - SOC refactoring
  public selectionManager: SelectionManager; // Phase 4 - SOC refactoring
  public bonusTurnManager: BonusTurnManager; // Phase 4 - SOC refactoring
  public fenHistoryManager: FENHistoryManager; // Phase 4 - SOC refactoring
  public turnController: TurnController; // Phase 4 - SOC refactoring
  private verboseLogging: boolean = false;
  private isDevelopment: boolean = false;

  constructor(config?: { isDevelopment?: boolean }) {
    // Accept isDevelopment as a config parameter instead of reading from React env
    if (config?.isDevelopment !== undefined) {
      this.isDevelopment = config.isDevelopment;
    }
    this.gameStateManager = new GameStateManager();
    this.bonusTurnManager = new BonusTurnManager(this.gameStateManager); // Phase 4 - SOC refactoring
    this.fenHistoryManager = new FENHistoryManager(); // Phase 4 - SOC refactoring
    this.turnController = new TurnController(this.gameStateManager, this.bonusTurnManager, this.fenHistoryManager); // Phase 4 - SOC refactoring
    this.eventManager = new EventManager(this.gameStateManager);
    this.movementManager = new MovementManager(this.gameStateManager, this.eventManager, this.bonusTurnManager, this.fenHistoryManager, this.turnController); // Phase 4 - SOC refactoring
    this.spawnAI = new SpawnAI(this.gameStateManager, this.eventManager, this);
    this.aiScheduler = new AIScheduler(this); // Phase 3 - SOC refactoring
    this.selectionManager = new SelectionManager(this.gameStateManager); // Phase 4 - SOC refactoring
  }

  // Game state and game modes
  getGameState(): GameState {
    return { ...this.gameStateManager.getGameState() };
  }

  getPendingTurnFENs(): string[] {
    return this.movementManager.getPendingTurnFENs();
  }

  /**
   * Gets the event manager for UI layer (EventBridge)
   * @returns The event manager
   */
  getEventManager(): EventManager {
    return this.eventManager;
  }

  /**
   * Sets tutorial configuration that persists in the actual game state
   * @param config Tutorial configuration object
   */
  setTutorialConfig(config: any): void {
    const realGameState = this.gameStateManager.getGameState();
    TutorialEngineAdapter.setTutorialConfig(realGameState, config);

    // Set up tutorial stalemate callback if provided
    if (config.stalemateCallback) {
      this.gameStateManager.setTutorialStalemateCallback(
        config.stalemateCallback
      );
    }
  }

  /**
   * Sets the game mode directly on the real game state
   * @param mode The game mode to set
   */
  setGameMode(mode: Mode): void {
    const realGameState = this.gameStateManager.getGameState();
    realGameState.mode = mode;
  }

  /**
   * Sets the matchmaking mode for multiplayer games
   * @param matchmakingMode The matchmaking mode ('competitive' or 'casual')
   */
  setMatchmakingMode(matchmakingMode: 'competitive' | 'casual'): void {
    const realGameState = this.gameStateManager.getGameState();
    realGameState.matchmakingMode = matchmakingMode;
  }

  /**
   * Resets the turn count to 0 on the real game state
   */
  resetTurnCount(): void {
    const realGameState = this.gameStateManager.getGameState();
    realGameState.turnCount = 0;
  }

  /**
   * Sets whether shadows are active (should be removed on next move)
   * Used by Firebase sync to restore shadow state from previous turn
   * @param isActive Whether shadows exist from a previous move
   */
  setShadowsActive(isActive: boolean): void {
    this.movementManager.setShadowsActive(isActive);
  }

  /**
   * Checks if there are zombie ink shadows that the current player must deal with.
   * Zombie ink shadows persist until the original owner makes a move.
   */
  hasZombieInkShadowsForCurrentTurn(): boolean {
    return this.movementManager.hasZombieInkShadowsForCurrentTurn();
  }

  /**
   * Handles the case when a player attempts to select/move a zombie ink shadow.
   * If the piece is a zombie ink shadow targeting the current player, triggers the trap:
   * - Clears all shadows
   * - Ends the turn
   * @returns true if trap was triggered, false otherwise
   */
  attemptMoveZombieInkShadow(piece: IPiece, position: Position): boolean {
    return this.movementManager.attemptMoveZombieInkShadow(piece, position);
  }

  /**
   * Triggers a timeout loss for the specified player
   * Used for testing timeout scenarios
   * @param loser The color that timed out ('light' or 'dark')
   */
  triggerTimeout(loser: 'light' | 'dark'): void {
    const realGameState = this.gameStateManager.getGameState();
    const winner = loser === 'light' ? 'dark' : 'light';

    realGameState.state = State.PostGame;
    realGameState.lightWon = winner === 'light';
    (realGameState as any).winCondition = 'timeout';

  }

  async switchToLiveMove(playerId: string): Promise<string> {
    const gameId = await this.eventManager.setupLiveMode(playerId);
    return gameId;
  }

  switchToDevMove() {
    this.eventManager.setupDevMode();
  }

  /**
   * Switches to AI mode and initializes the AI controller
   * @param playerId The ID of the human player
   * @param lightKing The type of king for the light player
   * @param darkKing The type of king for the dark player
   */
  switchToAIMode(
    playerId: string,
    lightKing: PieceType,
    darkKing: PieceType,
    specifiedBoard: ISquare[][] | null = null
  ) {
    // Reset the game first
    this.gameStateManager.resetGame();
    // Reset repeatString for new game
    (this.gameStateManager.getGameState() as any).repeatString = "";

    // Force empty the board to avoid any leftover state
    this.gameStateManager.emptyBoard();

    // Set it to AI mode
    this.gameStateManager.setMode(Mode.AI);

    // Set the players
    this.gameStateManager.setLightPlayer(playerId);
    this.gameStateManager.setDarkPlayer("AI");

    this.gameStateManager.setGameStateState(State.KingSelection);
    this.setKing(lightKing, true);
    this.setKing(darkKing, false);
    this.gameStateManager.setGameStateState(State.Playing);

    // Place 5 random pieces on each side (including kings)
    if (specifiedBoard) {
      this.gameStateManager.setupBoard(specifiedBoard);
    } else {
      this.spawnAI.placeRandomStartingPieces(lightKing, darkKing);
    }

    // Replace the empty FEN with the actual starting position
    this.fenHistoryManager.initializeWithCurrentState(this.gameStateManager.getGameState());

    // Initialize standard AI controller
    if (!this.aiController) {
      this.aiController = new AIController(this);
      this.aiController.setSearchIterations(500);
    }

    // Just notify local listeners - no remote updates
    this.eventManager.notifyLocalListeners();
  }

  /**
   * Switches to TestAI mode for local 2-player games (shows both kings)
   * @param playerId The player ID for the light player
   * @param lightKing The type of king for the light player
   * @param darkKing The type of king for the dark player
   * @param specifiedBoard Optional pre-configured board state
   */
  switchToTestAIMode(
    playerId: string,
    lightKing: PieceType,
    darkKing: PieceType,
    specifiedBoard: ISquare[][] | null = null
  ) {
    // Reset the game first
    this.gameStateManager.resetGame();
    // Reset repeatString for new game
    (this.gameStateManager.getGameState() as any).repeatString = "";

    // Force empty the board to avoid any leftover state
    this.gameStateManager.emptyBoard();

    // Set it to TestAI mode (allows both players to move, shows both kings)
    this.gameStateManager.setMode(Mode.TestAI);

    // Set the players
    this.gameStateManager.setLightPlayer(playerId);
    this.gameStateManager.setDarkPlayer("Player2");

    this.gameStateManager.setGameStateState(State.KingSelection);
    this.setKing(lightKing, true);
    this.setKing(darkKing, false);
    this.gameStateManager.setGameStateState(State.Playing);

    // Place 5 random pieces on each side (including kings)
    if (specifiedBoard) {
      this.gameStateManager.setupBoard(specifiedBoard);

      // setupBoard creates fresh Piece objects and only copies isKing + hasLilyJumped.
      // Rebuild king flags and virus infection relationships that were lost.
      const gs = this.gameStateManager.getGameState();
      for (let row = 0; row < gs.board.length; row++) {
        for (let col = 0; col < gs.board[row].length; col++) {
          // Set isKing flags based on gameState.lightKing/darkKing
          for (const piece of gs.board[row][col].Pieces) {
            if (piece.isLight && piece.type === gs.lightKing) {
              piece.isKing = true;
            } else if (!piece.isLight && piece.type === gs.darkKing) {
              piece.isKing = true;
            }
          }

          // Rebuild virus infection: if Virus + another piece share a square, mark host as infected
          const square = gs.board[row][col];
          const hasVirus = square.Pieces.some(p => p.type === PieceType.Virus);
          if (hasVirus && square.Pieces.length > 1) {
            for (const piece of square.Pieces) {
              if (piece.type !== PieceType.Virus) {
                piece.isInfected = true;
              }
            }
          }
        }
      }
    } else {
      this.spawnAI.placeRandomStartingPieces(lightKing, darkKing);
    }

    // CRITICAL FIX: Replace the empty FEN with the actual starting position
    // resetGame() initializes fenHistory with [emptyFen], but we want the starting position
    // This MUST happen AFTER pieces are placed (above)
    const gameState = this.gameStateManager.getGameState();

    // Defensive check: Ensure board is initialized before encoding FEN
    if (!gameState.board || gameState.board.length === 0 || !gameState.board[0]) {
    } else {
      // Phase 4 - SOC Refactoring: Use FENHistoryManager
      this.fenHistoryManager.initializeWithCurrentState(gameState);
    }

    // CRITICAL FIX: Don't notify here - let the caller trigger notification
    // This ensures only ONE notification happens AFTER board is fully set up
    // Prevents FENHistory from recording intermediate/empty states
    // this.eventManager.notifyLocalListeners(); // REMOVED
  }

  /**
   * Initialize the engine for Arena mode WITHOUT placing any pieces.
   * The caller is responsible for placing pieces via createPiece(),
   * then calling finalizeArenaSetup() to initialize FEN history.
   */
  initForArena(lightKing: PieceType, darkKing: PieceType) {
    this.gameStateManager.resetGame();
    (this.gameStateManager.getGameState() as any).repeatString = "";
    this.gameStateManager.emptyBoard();
    this.gameStateManager.setMode(Mode.TestAI);
    this.gameStateManager.setLightPlayer('arena-light');
    this.gameStateManager.setDarkPlayer('Player2');
    this.gameStateManager.setGameStateState(State.KingSelection);
    this.setKing(lightKing, true);
    this.setKing(darkKing, false);
    this.gameStateManager.setGameStateState(State.Playing);
    // No piece placement — caller uses createPiece() then finalizeArenaSetup()
  }

  /**
   * Finalize board setup after manual piece placement via createPiece().
   * Initializes FEN history with the current board state.
   */
  finalizeArenaSetup() {
    const gameState = this.gameStateManager.getGameState();
    if (!gameState.board || gameState.board.length === 0 || !gameState.board[0]) {
      return;
    }
    this.fenHistoryManager.initializeWithCurrentState(gameState);
  }

  /**
   * Switches to Watch mode for AI vs AI gameplay
   * @param lightKing The type of king for the light player
   * @param darkKing The type of king for the dark player
   */
  switchToWatchMode(
    lightKing: PieceType,
    darkKing: PieceType,
    specifiedBoard: ISquare[][] | null = null
  ) {
    // Reset the game first
    this.gameStateManager.resetGame();
    // Reset repeatString for new game
    (this.gameStateManager.getGameState() as any).repeatString = "";

    // Force empty the board to avoid any leftover state
    this.gameStateManager.emptyBoard();

    // Set it to Watch mode
    this.gameStateManager.setMode(Mode.Watch);

    // Set both players as AI
    this.gameStateManager.setLightPlayer("AI_Light");
    this.gameStateManager.setDarkPlayer("AI_Dark");

    this.gameStateManager.setGameStateState(State.KingSelection);
    this.setKing(lightKing, true);
    this.setKing(darkKing, false);
    this.gameStateManager.setGameStateState(State.Playing);

    // Place 5 random pieces on each side (including kings)
    if (specifiedBoard) {
      this.gameStateManager.setupBoard(specifiedBoard);
    } else {
      this.spawnAI.placeRandomStartingPieces(lightKing, darkKing);
    }

    // CRITICAL FIX: Replace the empty FEN with the actual starting position
    // resetGame() initializes fenHistory with [emptyFen], but we want the starting position
    // This MUST happen AFTER pieces are placed (above)
    const gameState = this.gameStateManager.getGameState();

    // Defensive check: Ensure board is initialized before encoding FEN
    if (!gameState.board || gameState.board.length === 0 || !gameState.board[0]) {
    } else {
      // Phase 4 - SOC Refactoring: Use FENHistoryManager
      this.fenHistoryManager.initializeWithCurrentState(gameState);
    }

    // Initialize standard AI controller
    if (!this.aiController) {
      this.aiController = new AIController(this);
      this.aiController.setSearchIterations(500);
    }

    // Just notify local listeners - no remote updates
    this.eventManager.notifyLocalListeners();
  }

  placeRandomStartingPieces(lightKing: PieceType, darkKing: PieceType) {
    this.spawnAI.placeRandomStartingPieces(lightKing, darkKing);
  }

  getRandomPositions(count: number, row: number): Position[] {
    return this.spawnAI.getRandomPositions(count, row);
  }

  // Event Handling
  subscribeToGameStateChanges(listener: GameStateListener): void {
    this.eventManager.subscribeToGameStateChanges(listener);
  }

  unsubscribeToGameStateChanges(listener: GameStateListener): void {
    this.eventManager.unsubscribeToGameStateChanges(listener);
  }

  ungracefulTermination() {
    if (this.eventManager.getGameId()) {
      this.gameStateManager.getGameState().state = State.Terminated;
    }
  }

  getGameId(): string | undefined {
    return this.eventManager.getGameId();
  }

  joinGame(gameId: string, playerId: string, assignAsDark: boolean = true) {
    this.eventManager.joinGame(gameId, playerId, assignAsDark);
  }

  // Piece getting
  placePiece(piece: IPiece, position: Position): void {
    // In dev, sandbox, or AI mode, set hasMoved to true for Frogs if they're not on their correct starting rank
    if (
      (this.gameStateManager.getGameState().mode === Mode.Dev ||
        this.gameStateManager.getGameState().mode === Mode.Sandbox ||
        this.gameStateManager.getGameState().mode === Mode.AI) &&
      piece.type === PieceType.Frog
    ) {
      // Light frogs should be on rank 0, dark frogs should be on rank 7
      const correctRank = piece.isLight ? 0 : 7;
      if (position.row !== correctRank) {
        piece.hasMoved = true;
        piece.hasLilyJumped = true;
      }
    }

    // Check if the target square already has pieces
    const currentPieces = this.gameStateManager.getPiecesAt(position);
    if (currentPieces.length > 0) {
      // Check if one of the pieces is a tree
      const hasTree = currentPieces.some((p) => p.type === PieceType.Tree);

      if (hasTree) {

        // If there's already a tree with a piece in it, don't allow placing another piece
        if (currentPieces.length > 1) {
          return;
        }

        // Check if the piece being placed can climb trees
        if (!allClimbingPieces.includes(piece.type)) {
          return;
        }

      }
    }

    this.gameStateManager.placePiece(piece, position);

    // Validate meadow effects after placement (for sandbox/dev mode)
    this.movementManager.validateMeadowEffects();

    // SANDBOX/DEV MODE: Add FEN history entry for piece placement
    const gameState = this.gameStateManager.getGameState();
    if (gameState.mode === Mode.Sandbox || gameState.mode === Mode.Dev) {
      const newFEN = ZodiacFEN.encodeWithoutKings(gameState);
      const algebraicPos = String.fromCharCode(65 + position.col) + (position.row + 1); // e.g., "D4"
      const pieceName = piece.isLight ? `Light ${piece.type}` : `Dark ${piece.type}`;

      // Add placement annotation to FEN: ~{placement:Type,Color,Position}
      // e.g., "8/8/8/8/8~{placement:Crane,Dark,C5}"
      const colorStr = piece.isLight ? 'Light' : 'Dark';
      // IMPORTANT: Don't include @turnCount for placement - placements are "setup", not moves
      const annotatedFEN = `${newFEN}~{placement:${piece.type},${colorStr},${algebraicPos}}`;

      // Create a new fenHistory array with the placement annotation
      gameState.fenHistory = [...gameState.fenHistory, annotatedFEN];
    }

    this.eventManager.notifyLocalListeners();
  }

  getPiecesAt(treePosition: { row: number; col: number }): IPiece[] {
    return this.gameStateManager.getPiecesAt(treePosition);
  }

  setKing(pieceType: PieceType, isLight: boolean) {
    var gameState = this.gameStateManager.getGameState();
    var text = isLight ? "light" : "dark";
    if (gameState.state !== State.KingSelection)
      throw Error(`The game state is incorrect to set a ${text} king`);

    if ((isLight && gameState.lightKing) || (!isLight && gameState.darkKing)) {
      // During rematch, Firebase listener can briefly restore stale king values
      // before the reset write propagates. Allow overwrite in KingSelection state.
    }

    if (isLight) gameState.lightKing = pieceType;
    else gameState.darkKing = pieceType;

    // this is essentially the end of king selection if both kings are set
    if (gameState.lightKing && gameState.darkKing) {
      this.gameStateManager.nextGameState();
      // HACK to get the game state setup for local dev
      // if (this.isDevelopment) {
      //   var newState = {
      //     ...this.gameStateManager.getGameState(),
      //     fenHistory: ["3Bb3/C6c/F✳️6f✳️/U6u/G6g"],
      //     state: State.Playing,
      //   };
      //   this.gameStateManager.setGameState(newState);
      // }
    }

    // CRITICAL FIX: Don't notify during TestAI mode initialization
    // This prevents FENHistory from recording empty board before pieces are placed
    // switchToTestAIMode will call notifyLocalListeners() after pieces are placed
    const isTestAIInitialization = gameState.mode === Mode.TestAI;

    if (!isTestAIInitialization) {
      this.eventManager.notifyLocalListeners();
      this.eventManager.sendUpdateToRemote();
    }
  }

  setStartingPiece(pieceType: PieceType, isLight: boolean, col: number) {
    const stateBefore = this.gameStateManager.getGameState().state;
    this.gameStateManager.setStartingPiece(pieceType, isLight, col);

    // When all pieces are placed, the state transitions to Playing.
    // Initialize FEN history with the actual starting position (replaces emptyFen).
    if (stateBefore === State.PieceSelection &&
        this.gameStateManager.getGameState().state === State.Playing) {
      this.fenHistoryManager.initializeWithCurrentState(this.gameStateManager.getGameState());
    }

    this.eventManager.sendUpdateToRemote(true);
    this.eventManager.notifyLocalListeners();
  }

  // Movement Handling
  getValidMoves(position: Position, piece: IPiece): IMove[] {
    const gameState = this.gameStateManager.getGameState();

    // TUTORIAL MODE: Check turn limits via adapter
    if (TutorialEngineAdapter.isInTutorialMode(gameState)) {
      const turnLimitResult = TutorialEngineAdapter.checkTurnLimit(gameState);
      if (turnLimitResult.shouldBlockAction) {
        // Failsafe: if turnCount has reached turnLimit while state is still
        // Playing, the move-success path missed its chance to fire the win
        // check (e.g. a failed move that pre-incremented turnCount). Trigger
        // it now so we transition to PostGame instead of stranding the player
        // with no legal moves and no game-end UI. checkGameEnd is idempotent
        // — it early-returns if state is already PostGame.
        if (gameState.state === State.Playing) {
          this.checkGameEnd();
        }
        return [];
      }
    }

    if (gameState.mode !== Mode.Live) {
      return this.movementManager.getValidMoves(position, piece);
    }

    if (gameState.mode === Mode.Live && gameState.state === State.Playing) {
      return this.movementManager.getValidMoves(position, piece);
    }

    return [];
  }

  async makeMove(
    fromPosition: Position,
    toPosition: Position,
    piece: IPiece,
    toPiece: IPiece | null,
    moveType: MoveType,
    sideEffects: ISideEffect[]
  ): Promise<boolean> {
    // Log every move attempt at the very start - using console.warn for visibility
    const moverType = piece.isLight ? '👤 PLAYER (Light)' : '🤖 AI (Dark)';
    const gameStateForLog = this.gameStateManager.getGameState();

    // Handle Tutorial mode turn increment BEFORE making the move via adapter
    const gameState = this.gameStateManager.getGameState();
    const tutorialTurnCountBeforeIncrement = TutorialEngineAdapter.isInTutorialMode(gameState)
      ? gameState.turnCount
      : null;
    if (TutorialEngineAdapter.isInTutorialMode(gameState)) {
      TutorialEngineAdapter.handleTutorialTurnIncrement(
        gameState,
        piece,
        moveType,
        () => this.gameStateManager.incrementTurnCount()
      );
    }

    const moveMade = this.movementManager.makeMove(
      fromPosition,
      toPosition,
      piece,
      toPiece,
      moveType,
      sideEffects
    );

    // Revert the tutorial-mode pre-increment if the move failed. Otherwise
    // turnCount can reach turnLimit without a checkGameEnd ever firing — the
    // player ends up selectable-but-immovable with the game stuck in Playing,
    // because getValidMoves blocks at turnCount >= turnLimit while state
    // never transitions to PostGame to surface the win/loss.
    if (!moveMade && tutorialTurnCountBeforeIncrement !== null) {
      gameState.turnCount = tutorialTurnCountBeforeIncrement;
    }
    if (moveMade) {
      // Log every move clearly to track player vs AI moves
      const moverType = piece.isLight ? '👤 PLAYER (Light)' : '🤖 AI (Dark)';
      // Record last move info directly on the real game state for UI effects/debugging
      try {
        const realGameState = this.gameStateManager.getGameState();
        const captureEffects = (sideEffects || []).filter(
          (se) => se.effect && se.effect.type === EffectType.capture
        );
        (realGameState as any).lastMove = {
          fromPosition,
          toPosition,
          mover: {
            type: piece.type,
            isLight: piece.isLight,
          },
          moveType,
          captureEffects: captureEffects.map((se) => ({
            position: { row: se.position.row, col: se.position.col },
            capturedPieces: (se.piecesToEffect || []).map((p) => ({
              id: p.id,
              type: p.type,
              isLight: p.isLight,
            })),
          })),
        };

        // Track the color of the last move for FEN delta inference
        realGameState.lastMoveWasLight = piece.isLight;
      } catch (e) {
        // Non-fatal: best-effort logging only
      }

      // Notify listeners and send updates if necessary
      this.eventManager.notifyLocalListeners();
      await this.eventManager.sendUpdateToRemote(true);

      // CRITICAL: Check game end BEFORE runPostTurn() to detect zombie race victories
      // runPostTurn() clears zombification status, so we must check first
      this.checkGameEnd();

      // TUTORIAL MODE: Check for immediate failure after move completion via adapter
      if (TutorialEngineAdapter.isInTutorialMode(gameState)) {
        const tutorialConfig = TutorialEngineAdapter.getTutorialConfig(gameState);
        const failureResult = await TutorialEngineAdapter.checkTutorialFailure(
          gameState,
          piece,
          tutorialConfig?.uiLogger
        );

        if (failureResult.tutorialFailed) {
          return moveMade; // Don't continue with normal game end checking
        }
      }

      this.runPostTurn();

      // CRITICAL FIX: If game ended during tutorial, stop immediately to prevent AI from moving after victory
      if (this.gameStateManager.getGameState().state === State.PostGame) {
        return moveMade;
      }
    }

    // Dev and Sandbox mode still need manual turn increment (special case)
    const currentMode = this.gameStateManager.getGameState().mode;
    if (
      currentMode === Mode.Dev ||
      currentMode === Mode.Sandbox
    ) {
      this.gameStateManager.incrementTurnCount();
    }

    return moveMade;
  }

  checkGameEnd() {
    if (this.gameStateManager.getGameState().state === State.PostGame) {
      // Already game over, nothing more to do
      return;
    }

    // Check for time expiration FIRST (highest priority game end condition)
    const timeControlState = TimeControlManager.getState();
    if (timeControlState.timeExpired && timeControlState.winner) {
      const gameState = this.gameStateManager.getGameState();
      gameState.state = State.PostGame;
      gameState.lightWon = timeControlState.winner === 'light';
      (gameState as any).winCondition = 'timeout';  // Mark this as a timeout victory
      TimeControlManager.stopOnGameEnd();
      this.gameStateManager.nextGameState();
      this.eventManager.notifyLocalListeners();
      this.eventManager.sendUpdateToRemote();
      return;
    }

    // RULES MODE: Skip all game end checks to allow animations to complete
    const currentGameState = this.gameStateManager.getGameState();
    if (currentGameState.mode === Mode.Rules) {
      return;
    }

    // SANDBOX MODE: Skip victory detection but still change turns
    if (currentGameState.mode === Mode.Sandbox) {
      this.changeTurns();
      return;
    }

    // TUTORIAL MODE: Delegate to adapter for all tutorial logic
    if (TutorialEngineAdapter.isInTutorialMode(currentGameState)) {

      // Get tutorial config to check if it's solo mode
      const tutorialConfig = TutorialEngineAdapter.getTutorialConfig(currentGameState);
      const isSoloMode = tutorialConfig?.soloMode || false;

      // CRITICAL FIX: Only check hasGameEnded() for SOLO tutorials
      // For non-solo tutorials (with AI), we ONLY use TutorialWinConditions
      // This prevents premature PostGame state which blocks AI moves
      if (isSoloMode && this.gameStateManager.hasGameEnded()) {
        const winningColor = currentGameState.lightWon ? "Light" : "Dark";

        if (tutorialConfig?.uiLogger) {
          tutorialConfig.uiLogger(`🏆 Tutorial Complete! Winner: ${winningColor}`, "success");
        }

        currentGameState.state = State.PostGame;
        this.eventManager.notifyLocalListeners();
        this.eventManager.sendUpdateToRemote();
        return;
      }

      // Check tutorial-specific win conditions via adapter
      const winResult = TutorialEngineAdapter.checkTutorialWinConditions(
        currentGameState,
        () => this.eventManager.notifyLocalListeners(),
        () => this.eventManager.sendUpdateToRemote()
      );

      if (winResult.tutorialEnded) {
        return;
      }

      // Stalemate detection for non-solo tutorials. hasGameEnded() is skipped
      // above to avoid premature PostGame, but stalemate still has to be
      // caught — otherwise a poisoned/entangled lone king sits idle forever.
      // hasNextPlayerStalemate sets PostGame + lightWon directly so the AI
      // Thinking overlay clears and the TutorialGame React effect that
      // watches `state === PostGame && lightWon === false` triggers the loss
      // UI as a backstop. Also fires the tutorialStalemateCallback. Fall
      // through to changeTurns — the turn flip is harmless once PostGame is
      // set and makeMove's PostGame check will short-circuit any followup.
      if (!isSoloMode) {
        this.gameStateManager.hasNextPlayerStalemate();
      }

      // Handle tutorial turn changes via adapter
      if (tutorialConfig?.uiLogger) {
        tutorialConfig.uiLogger(
          `🎓 Tutorial Turn Debug: Before changeTurns - isLightTurn=${currentGameState.isLightTurn}, turnCount=${currentGameState.turnCount}`,
          "info"
        );
      }

      this.changeTurns();

      if (tutorialConfig?.uiLogger) {
        const afterState = this.gameStateManager.getGameState();
        tutorialConfig.uiLogger(
          `🎓 Tutorial Turn Debug: After changeTurns - isLightTurn=${afterState.isLightTurn}, turnCount=${afterState.turnCount}`,
          "info"
        );
      }
      return;
    }

    if (this.gameStateManager.hasGameEnded()) {
      // CRITICAL: Clear bonus turn state when game ends
      // This prevents bonus turn flag from persisting into the final delta
      if (this.bonusTurnManager.hasBonusTurn()) {
        this.bonusTurnManager.clearBonusTurn();
      }

      if (this.gameStateManager.getGameState().state === State.Draw) {
        TimeControlManager.stopOnGameEnd();
        this.eventManager.notifyLocalListeners();
        this.eventManager.sendUpdateToRemote();
        return;
      }
      this.gameStateManager.getGameState().state = State.PostGame;
      const winningColor = this.gameStateManager.getGameState().lightWon
        ? "Light"
        : "Dark";
      // Keep this log since it's actual game outcome

      // Stop the timer when game ends
      TimeControlManager.stopOnGameEnd();
      this.gameStateManager.nextGameState();
      this.eventManager.notifyLocalListeners();
      this.eventManager.sendUpdateToRemote();
    } else {
      // Change turns for both Live and AI modes
      if (this.verboseLogging) {
      }
      this.changeTurns();
    }
  }

  runPostTurn() {
    const gameState = this.gameStateManager.getGameState();
    const isInBonusTurn = gameState.activeBonusTurnPieceId !== null;

    var pieces = this.gameStateManager.getAllPieces();
    pieces.forEach((piece) => {
      // DEBUG: Log poison check in runPostTurn
      if (piece.poisonedStatus.isPoisoned) {
      }

      // this stinks, but the code is short and simple so it can remain for now
      if (
        piece.poisonedStatus.removeOnTurn ===
        this.gameStateManager.getGameState().turnCount
      ) {
        piece.poisonedStatus = {
          isPoisoned: false,
          removeOnTurn: undefined,
        };
      }

      // CRITICAL: Don't remove zombification during bonus turns!
      // Zombification should only be removed when the turn FULLY ends
      if (
        !isInBonusTurn &&
        piece.zombifiedStatus.removeOnTurn ===
        this.gameStateManager.getGameState().turnCount
      ) {
        piece.zombifiedStatus = {
          isZombified: false,
          removeOnTurn: undefined,
        };
        piece.isZombified = false;
      }
    });
  }

  changeTurns() {
    // Phase 4 - SOC Refactoring: Delegate to TurnController
    const gameState = this.gameStateManager.getGameState();
    const fromPlayer = gameState.isLightTurn ? 'light' : 'dark'; // Track before turn change

    // Use TurnController to handle all turn logic
    this.turnController.changeTurns(
      () => this.movementManager.finalizeTurnFENHistory(),
      () => this.eventManager.notifyLocalListeners(),
      (moveMade?: boolean) => this.eventManager.sendUpdateToRemote(moveMade)
    );

    // Handle time control turn change for ALL game modes (except Sandbox/bonus turns)
    if (!this.bonusTurnManager.hasBonusTurn() && gameState.mode !== Mode.Sandbox) {
      const toPlayer = gameState.isLightTurn ? 'light' : 'dark'; // Get after turn change
      TimeControlManager.handleTurnChange(fromPlayer, toPlayer);
    }
  }

  /**
   * Makes a move using the AI controller
   * @returns Whether a move was successfully made
   */
  async makeAIMove(): Promise<boolean> {
    // Log debug info
    const currentGameState = this.gameStateManager.getGameState();

    // CRITICAL CHECK: If game has ended, AI should never move
    if (currentGameState.state === State.PostGame) {
      return false;
    }

    // TUTORIAL MODE: Delegate blocking checks to adapter
    if (TutorialEngineAdapter.isInTutorialMode(currentGameState)) {
      const blockResult = TutorialEngineAdapter.shouldBlockAIMove(currentGameState);
      if (blockResult.shouldBlockAction) {
        return false;
      }
    }

    // Never make AI moves during a HUMAN bonus turn. AI's own bonus turns
    // (e.g. Dark penguin rotate after a slide, monkey swing/consume, virus
    // infect) must pass through so the AI can complete the bonus move —
    // otherwise the game freezes with isLightTurn unchanged and the
    // "AI THINKING…" overlay stuck on.
    if (currentGameState.activeBonusTurnPieceId) {
      let bonusPieceIsLight: boolean | null = null;
      outer: for (const row of currentGameState.board) {
        for (const square of row) {
          for (const piece of square.Pieces) {
            if (piece.id === currentGameState.activeBonusTurnPieceId) {
              bonusPieceIsLight = piece.isLight;
              break outer;
            }
          }
        }
      }
      // In playVsAI / Tutorial modes the AI plays Dark, so only block when
      // the bonus belongs to Light. Watch mode (AI vs AI) must always pass
      // through so both sides' bonus turns get played.
      if (bonusPieceIsLight === true && currentGameState.mode !== Mode.Watch) {
        return false;
      }
    }

    // Additional check: In regular AI mode, AI should only move during Dark turns
    // In Watch mode, AI plays both sides
    if (currentGameState.mode !== Mode.Watch && currentGameState.isLightTurn) {
      return false;
    }

    // TUTORIAL MODE: Check for overrides and use tutorial AI via adapter
    if (TutorialEngineAdapter.isInTutorialMode(currentGameState)) {
      // Check for tutorial FEN overrides first
      const overrideResult = await TutorialEngineAdapter.checkAndApplyTutorialOverride(
        currentGameState,
        this
      );

      if (overrideResult.shouldBlockAction) {
        return true;
      }

      // Use tutorial-specific AI (Sentinel or MCTS)
      const aiResult = await TutorialEngineAdapter.makeTutorialAIMove(
        currentGameState,
        this
      );

      if (aiResult) {
        return true;
      } else {
        return false;
      }
    }

    // Use AIOrchestrator for proper AI routing
    if (!this.aiOrchestrator) {
      this.aiOrchestrator = new AIOrchestrator(this);
    }

    try {
      const startTime = performance.now();

      // Request AI move from orchestrator - it will route to correct AI implementation
      const timeLimit = this.aiTimeLimits
        ? (currentGameState.isLightTurn ? this.aiTimeLimits.light : this.aiTimeLimits.dark)
        : 10000;
      const aiMoveResult = await this.aiOrchestrator.requestAIMove(currentGameState, timeLimit);

      const endTime = performance.now();
      const thinkingTimeMs = Math.round(endTime - startTime);

      if (!aiMoveResult) {
        return false;
      }

      // Check if AI made a king guess taunt
      if (aiMoveResult.evaluationBreakdown?.kingGuess) {
        const guess = aiMoveResult.evaluationBreakdown.kingGuess;
        this.eventManager.broadcastKingGuess(guess, currentGameState.isLightTurn);
      }

      // Deduct AI thinking time from the clock
      const currentPlayer = currentGameState.isLightTurn ? 'light' : 'dark';
      TimeControlManager.deductTime(currentPlayer, thinkingTimeMs);

      // Get the piece at the source position
      const pieces = this.getPiecesAt(aiMoveResult.fromPosition);
      if (pieces.length === 0) {
        return false;
      }

      // Execute the move in the game engine
      const isMoveMade = await this.makeMove(
        aiMoveResult.fromPosition,
        aiMoveResult.move.position,
        pieces[0],
        aiMoveResult.move.toPiece,
        aiMoveResult.move.moveType,
        aiMoveResult.move.sideEffects
      );

      if (!isMoveMade) {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Sets the number of search iterations for the AI
   * @param iterations Number of iterations to perform during search
   */
  setAISearchIterations(iterations: number): void {
    if (!this.aiController) {
      this.aiController = new AIController(this);
    }
    this.aiController.setSearchIterations(iterations);
  }

  /**
   * Sets the exploration parameter for the AI's MCTS algorithm
   * Higher values (>1.0) favor exploration of new moves
   * Lower values (<1.0) favor exploitation of known good moves
   *
   * @param parameter The exploration parameter value (default is Math.sqrt(2) ≈ 1.414)
   */
  setAIExplorationParameter(parameter: number): void {
    if (!this.aiController) {
      this.aiController = new AIController(this);
    }
    this.aiController.setExplorationParameter(parameter);
  }

  /**
   * Get the AI controller
   */
  getAIController() {
    return this.aiController;
  }

  /**
   * Sets verbose logging for all components
   * @param isVerbose Whether to enable verbose logging
   */
  setVerboseLogging(isVerbose: boolean): void {
    this.verboseLogging = isVerbose;

    // Set verbose logging for standard AI controller
    if (this.aiController) {
      this.aiController.setVerboseLogging(isVerbose);
    }
  }

  /**
   * Get the AI stats from the current controller
   */
  getAIStats() {
    return this.aiController?.getLastMoveStats() || null;
  }

  /**
   * Creates a new piece of the specified type and places it on the board
   * @param pieceType The type of piece to create
   * @param isLight Whether the piece belongs to the light player
   * @param position The position to place the piece at
   * @returns The created piece
   */
  createPiece(pieceType: string, isLight: boolean, position: Position): IPiece {
    // Convert string type to PieceType enum if needed
    const type = this.getPieceTypeFromString(pieceType);

    // Set default direction for penguins
    let direction: Direction | null = null;
    if (type === PieceType.Penguin) {
      direction = isLight ? ("down" as Direction) : ("up" as Direction);
      // Debug: Log penguin creation
    }

    // Create the new piece
    const piece = new Piece(type, isLight, direction);

    // Debug: Always log Frog creation
    if (type === PieceType.Frog) {
    }

    // Set lilypad property for Frogs in Rules demonstrations
    // Check if we're in a Rules context by looking at the current mode and context
    const currentMode = this.gameStateManager.getGameState().mode;
    const gameState = this.gameStateManager.getGameState();


    // Apply lilypad rules if:
    // 1. We're in Rules mode, OR
    // 2. We're in TestAI mode with specific characteristics indicating Rules demo
    const isRulesContext =
      currentMode === Mode.Rules ||
      (currentMode === Mode.TestAI && this.detectSimpleRulesDemo(gameState));

    if (type === PieceType.Frog && isRulesContext) {
      // If spawned on any row except 0, set hasLilyJumped = true (no lilypad graphic)
      if (position.row !== 0) {
        piece.hasLilyJumped = true;
      } else {
        piece.hasLilyJumped = false;
      }
    } else if (type === PieceType.Frog) {
    }

    // Additional debug for penguins
    if (type === PieceType.Penguin) {
    }

    // Place it on the board
    this.gameStateManager.placePiece(piece, position);

    // Debug: Check Frog state after placing on board
    if (type === PieceType.Frog) {
    }

    return piece;
  }

  /**
   * Helper method to detect if we're in a simple Rules demonstration context
   * This is used to distinguish between actual TestAI games and Rules demos
   */
  private detectSimpleRulesDemo(gameState: GameState): boolean {
    // Count total pieces on the board
    let totalPieces = 0;
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        totalPieces += gameState.board[row][col].Pieces.length;
      }
    }

    // Rules demos typically have very few pieces (1-5) compared to full games
    // Also check if turnCount is 0 (fresh demo scenario)
    const hasVeryFewPieces = totalPieces <= 5;
    const isNewScenario = gameState.turnCount === 0;

    return hasVeryFewPieces && isNewScenario;
  }

  /**
   * Converts a string representation of a piece type to the PieceType enum value
   * @param pieceType String representation of the piece type
   * @returns The corresponding PieceType enum value
   */
  private getPieceTypeFromString(pieceType: string): PieceType {
    switch (pieceType) {
      case "Bear":
        return PieceType.Bear;
      case "Crane":
        return PieceType.Crane;
      case "Frog":
        return PieceType.Frog;
      case "Goat":
        return PieceType.Goat;
      case "Monkey":
        return PieceType.Monkey;
      case "Fungi":
        return PieceType.Fungi;
      case "Octopus":
        return PieceType.Octopus;
      case "Penguin":
        return PieceType.Penguin;
      case "Rhino":
        return PieceType.Rhino;
      case "Snake":
        return PieceType.Snake;
      case "Tree":
        return PieceType.Tree;
      case "Wolf":
        return PieceType.Wolf;
      case "Virus":
        return PieceType.Virus;
      default:
        throw new Error(`Unknown piece type: ${pieceType}`);
    }
  }

  /**
   * Resets the game and starts a new game with random kings
   * Used for autoplay between rounds
   */
  resetAndStartNewGame(): void {
    // Get all eligible king types (excluding certain types)
    const eligibleKings = [
      PieceType.Bear,
      PieceType.Crane,
      PieceType.Frog,
      PieceType.Goat,
      PieceType.Monkey,
      PieceType.Fungi,
      PieceType.Rhino,
      PieceType.Snake,
      PieceType.Tree,
      PieceType.Wolf,
    ];

    // Select random kings
    const randomLightKingIndex = Math.floor(
      Math.random() * eligibleKings.length
    );
    const lightKing = eligibleKings[randomLightKingIndex];

    const randomDarkKingIndex = Math.floor(
      Math.random() * eligibleKings.length
    );
    const darkKing = eligibleKings[randomDarkKingIndex];


    // Call switchToAIMode
    this.switchToAIMode(
      this.getGameState().lightPlayer || "player",
      lightKing,
      darkKing
    );
  }

  /**
   * Resets the game to an empty board state (8/8/8/8/8)
   * Used when user wants to end the game without starting a new one
   */
  resetToEmptyBoard(): void {
    this.gameStateManager.emptyBoard();
    this.gameStateManager.setGameStateState(State.PieceSelection);
    this.gameStateManager.getGameState().turnCount = 0;
    this.gameStateManager.getGameState().isLightTurn = true;
  }

  /**
   * Gets all pieces for the current player's turn
   * Used primarily for debugging AI move issues
   * @returns Array of pieces that belong to the current player
   */
  getPiecesForCurrentPlayer(): { type: PieceType; position: Position }[] {
    const gameState = this.gameStateManager.getGameState();
    const isLightTurn = gameState.isLightTurn;
    const result: { type: PieceType; position: Position }[] = [];

    // Iterate through the board and find all pieces for the current player
    for (let row = 0; row < gameState.board.length; row++) {
      for (let col = 0; col < gameState.board[row].length; col++) {
        const square = gameState.board[row][col];
        if (square.Pieces && square.Pieces.length > 0) {
          const piece = square.Pieces[0]; // Get the top piece
          const isOwnColor = piece.isLight === isLightTurn;
          const isZombie = piece.zombifiedStatus.isZombified;
          const shouldInclude = isOwnColor || isZombie;
          // Allow control if piece is the current player's color, or if it is zombified
          if (shouldInclude) {
            result.push({
              type: piece.type,
              position: { row, col },
            });
          }
        }
      }
    }

    return result;
  }

  /**
   * Sets the heuristic for the AI
   * @param type The type of heuristic to use
   */
  setAIHeuristic(type: HeuristicType): void {
    if (!this.aiController) {
      this.aiController = new AIController(this);
    }
    this.aiController.setHeuristic(type);
  }

  /**
   * Get the current AI heuristic type
   * @returns The current heuristic type
   */
  getAIHeuristic(): HeuristicType {
    if (!this.aiController) {
      return HeuristicType.Plain;
    }
    return this.aiController.getHeuristicType();
  }

  /**
   * Enable or disable debug mode for the current AI heuristic
   * @param enable Whether to enable debugging
   */
  setAIHeuristicDebug(enable: boolean): void {
    if (!this.aiController) {
      return;
    }
    this.aiController.setHeuristicDebug(enable);
  }

  /**
   * Loads a game state from a FEN string
   * @param fenString The FEN string to load
   * @returns Success status and any error message
   */
  loadFromFEN(fenString: string): { success: boolean; error?: string } {
    try {
      // Validate the FEN string
      if (!ZodiacFEN.validate(fenString)) {
        return { success: false, error: "Invalid FEN format" };
      }

      // Always clear the board first to avoid stacking pieces
      this.gameStateManager.emptyBoard();

      // Decode the FEN into a new game state
      loadTestScenarioFEN(this, fenString);
      var newGameState = this.getGameState();

      // Set king types based on pieces marked as isKing
      let foundLightKingType: string | null = null;
      let foundDarkKingType: string | null = null;

      for (let row = 0; row < newGameState.board.length; row++) {
        for (let col = 0; col < newGameState.board[row].length; col++) {
          for (const piece of newGameState.board[row][col].Pieces) {
            if (piece.isKing) {
              if (piece.isLight) foundLightKingType = piece.type;
              else foundDarkKingType = piece.type;
            }
          }
        }
      }

      if (foundLightKingType) {
        newGameState.lightKing =
          (PieceType as any)[foundLightKingType] || foundLightKingType;
      }
      if (foundDarkKingType) {
        newGameState.darkKing =
          (PieceType as any)[foundDarkKingType] || foundDarkKingType;
      }

      // Set to dev mode for FEN loading (allows free interaction)
      newGameState.mode = Mode.Dev;
      newGameState.state = State.Playing;
      newGameState.lightWon = undefined;

      // Reset repeatString for new game state
      (newGameState as any).repeatString = "";

      // Update the game state first
      this.gameStateManager.setGameState(newGameState);

      // Phase 4 - SOC Refactoring: Use FENHistoryManager
      // Initialize fenHistory with the loaded FEN as initial position
      this.fenHistoryManager.initializeWithCurrentState(newGameState);

      // Notify listeners
      this.eventManager.notifyLocalListeners();

      return { success: true };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }
}
